import { Injectable, inject } from '@angular/core';
import { InsuranceCase } from '../../app-create-case/models/insurance-case.model';
import { EcapQueryHandlerResponse, Filter, GqlWatchResponse } from '../../shared/models';
import { GqlQueryBuilderService } from '../../shared/services/gql-query-builder.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { UpdateSLACommand } from '../../shared/models/commandModels/UpdateSLACommand';
import { IpexCommandResponse } from '../../shared/models/responseModels/IpexCommandResponse';
import { Observable, Subject } from 'rxjs';
import { CommonService } from '../../shared/services/common.service';
import { GetCasesWithFieldsQueryModel } from '../../shared/models/request.model';
import { GqlQueryCommandService } from '../../shared/services/gql-query-command.service';
import { UrlResolverService } from '../../shared/services/url/url-resolver.service';

@Injectable({
    providedIn: 'root'
})
export class CaseQueryService {
    private readonly urlResolver = inject(UrlResolverService);
    header: any = new HttpHeaders({
        'Content-Type': 'application/json'
    });
    pcxQueryUrl: string = this.urlResolver.getPcxQueryUrl();
    pcxCommandUrl: string = this.urlResolver.getPcxCommandUrl();
    caseQueryUrl: string = this.urlResolver.getCaseQueryUrl();
    fraudCases$: Subject<[InsuranceCase[], string]> = new Subject<[InsuranceCase[], string]>();
    selectedFraudCaseItemIds$: Subject<string[]> = new Subject<string[]>();

    constructor(
        private gqlQueryBuilderService: GqlQueryBuilderService,
        private http: HttpClient,
        private readonly gqlQueryCommandService: GqlQueryCommandService
    ) {}

    escapeRegExp(text) {
        return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    public getInsuranceCaseList(claimNumber: string, insuranceCompanyId: string | null = null) {
        const filters = [new Filter('IsDraft', '=', false)];
        claimNumber = CommonService.sanitizeSearchString(claimNumber);
        if (claimNumber.includes('/')) {
            filters.push(new Filter('InsuranceClaimNumber', '=', claimNumber));
        } else {
            filters.push(new Filter('InsuranceClaimNumber', 'search', claimNumber));
        }

        if (insuranceCompanyId !== null) {
            filters.push(new Filter(`'CaseReferenceInfo.InsuranceComapnayId'`, '=', insuranceCompanyId as string));
        }
        const sortBy = '{CreateDate: -1}';
        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filters)}}`,
            PageNumber: 0,
            PageSize: 100
        };
        const url = this.pcxQueryUrl + `GetInsuranceCase`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'body' }).pipe(
            map((response: EcapQueryHandlerResponse<InsuranceCase[]>) => {
                return response && response.Results;
            })
        );
    }

    public saveDelayReason(requestModel: UpdateSLACommand): Observable<IpexCommandResponse<InsuranceCase>> {
        return this.http.post<IpexCommandResponse<InsuranceCase>>(
            this.pcxCommandUrl + 'UpdateSLAStatus',
            requestModel,
            {
                headers: this.header,
                withCredentials: true
            }
        );
    }

    getInsuranceCaseListForDuplicateFraudOffers(
        CurrentInsuranceCaseId: string,
        ServiceProviderId: string,
        SubTotalPrice: any
    ) {
        let filterString = `{ '_id':{$ne:'${CurrentInsuranceCaseId}'}, "IsMarkedToDelete":false,"IsDraft":false,"CaseServiceList":{"$elemMatch":{"SubTotalPrice": ${SubTotalPrice},"ServiceAdditionalData.ServiceProviderItemId": '${ServiceProviderId}' }}}`;
        const sortBy = '{CreateDate: -1}';
        const payload = {
            SortBy: sortBy,
            FilterString: filterString,
            PageNumber: 0,
            PageSize: 100
        };
        const url = this.pcxQueryUrl + `GetInsuranceCase`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'body' }).pipe(
            map((response: EcapQueryHandlerResponse<InsuranceCase[]>) => {
                return response && response.Results;
            })
        );
    }

    getInsuranceCaseListByConnected(connectedCaseIds: string[]) {
        const filters = [new Filter('IsDraft', '=', false), new Filter(`'_id'`, 'in', connectedCaseIds)];
        const sortBy = '{CreateDate: -1}';
        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filters)}}`,
            PageNumber: 0,
            PageSize: 100
        };
        const url = this.pcxQueryUrl + `GetInsuranceCase`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'body' }).pipe(
            map((response: EcapQueryHandlerResponse<InsuranceCase>) => {
                return response && response.Results;
            })
        );
    }

    updateCaseDocument(payload: any) {
        const url = this.pcxCommandUrl + `UpdateCaseDocument`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'body' }).pipe(
            map((response: EcapQueryHandlerResponse<InsuranceCase>) => {
                return response && response.Results;
            })
        );
    }
    getConnectedCaseList(claimNumber: string, insuranceCompanyId: string | null = null) {
        const filters = [new Filter('IsDraft', '=', false)];
        claimNumber = CommonService.sanitizeSearchString(claimNumber);
        if (claimNumber.includes('/')) {
            filters.push(new Filter('InsuranceClaimNumber', '=', claimNumber));
        } else {
            filters.push(new Filter('InsuranceClaimNumber', 'search', claimNumber));
        }

        if (insuranceCompanyId !== null) {
            filters.push(new Filter(`'CaseReferenceInfo.InsuranceComapnayId'`, '=', insuranceCompanyId as string));
        }
        const sortBy = '{CreateDate: -1}';
        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filters)}}`,
            PageNumber: 0,
            PageSize: 100,
            FieldSet: [
                'InsuranceClaimNumber',
                'OrderDate',
                'InspectorUserId',
                'InspectorUserName',
                'ZipCodeOfIssuer',
                'CaseStatus'
            ]
        };
        const url = this.caseQueryUrl + `GetCasesWithFields`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'body' }).pipe(
            map((response: any) => {
                return {
                    Data: response.Data,
                    TotalCount: response.TotalCount
                };
            })
        );
    }
    getInsuranceCaseListByConnectedWithProjection(connectedCaseIds: string[]) {
        const filters = [new Filter('IsDraft', '=', false), new Filter(`'_id'`, 'in', connectedCaseIds)];
        const sortBy = '{CreateDate: -1}';
        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filters)}}`,
            PageNumber: 0,
            PageSize: 100,
            FieldSet: [
                'InsuranceClaimNumber',
                'OrderDate',
                'InspectorUserId',
                'InspectorUserName',
                'ZipCodeOfIssuer',
                'CaseStatus'
            ]
        };
         const url = this.caseQueryUrl + `GetCasesWithFields`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'body' }).pipe(
            map((response: any) => {
                return {
                    Data: response.Data,
                    TotalCount: response.TotalCount
                };
            })
        );
    }
    getCollectiveBillCases(payload: GetCasesWithFieldsQueryModel) {
        const url = this.caseQueryUrl + `GetCasesWithFields`;
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'body' }).pipe(
            map((response: any) => {
                return {
                    Data: response.Data,
                    TotalCount: response.TotalCount
                };
            })
        );
    }
    getUploadedCases(payload: GetCasesWithFieldsQueryModel, dataTableModel: any) {
        const url = `${this.caseQueryUrl}GetCasesWithFields`;
        return this.http
            .post<any>(url, payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'body'
            })
            .pipe(
                map(response => {
                    // Prepare the final parsed data
                    const preparedData = this.gqlQueryCommandService.parseGQLDataTableResponseData(
                        new GqlWatchResponse(response.Data, response.TotalCount),
                        dataTableModel
                    );

                    return preparedData;
                })
            );
    }
}
